import React, { useState } from 'react';
import { LogIn, UserPlus, KeyRound, Mail, Eye, EyeOff, Box, ShieldCheck } from 'lucide-react';
import { authenticate, createUser, resetPassword, seedOwnerAccount } from '../store/auth';
import { THEMES } from '../theme';

seedOwnerAccount();

export default function LoginScreen({ onLogin }) {
    const [mode, setMode] = useState('login'); // login | create | reset
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [show, setShow] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const t = THEMES.dark;

    const inputStyle = { width: '100%', padding: '12px 12px 12px 40px', background: t.inputBg, border: `1px solid ${t.borderLight}`, borderRadius: 12, color: t.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' };

    const submit = (e) => {
        e.preventDefault();
        setError(''); setInfo('');
        if (mode === 'login') {
            const r = authenticate(email, password);
            if (r.ok) onLogin(r.email); else setError(r.error);
        } else if (mode === 'create') {
            if (password.length < 8) return setError('Password must be at least 8 characters.');
            if (password !== confirm) return setError('Passwords do not match.');
            const r = createUser(email, password);
            if (r.ok) { setInfo('Account created — sign in now.'); setMode('login'); } else setError(r.error);
        } else {
            if (password.length < 8) return setError('New password must be at least 8 characters.');
            if (password !== confirm) return setError('Passwords do not match.');
            const r = resetPassword(email, password);
            if (r.ok) { setInfo('Password reset — sign in with your new password.'); setMode('login'); } else setError(r.error);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.bg, backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(37,99,235,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(99,102,241,0.12), transparent 40%)', fontFamily: "'Inter', system-ui, sans-serif" }}>
            <div style={{ width: 400, maxWidth: '92vw', background: t.surface, border: `1px solid ${t.borderLight}`, borderRadius: 24, padding: 36, boxShadow: t.shadow }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #2563eb, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Box size={24} color="#fff" /></div>
                    <div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: t.text }}>RooCAD</div>
                        <div style={{ fontSize: 11, color: t.textMuted }}>2D Sketch · 3D Modeling · AI</div>
                    </div>
                </div>
                <h1 style={{ fontSize: 18, fontWeight: 700, color: t.text, margin: '22px 0 4px' }}>
                    {mode === 'login' ? 'Welcome back' : mode === 'create' ? 'Create your account' : 'Reset your password'}
                </h1>
                <p style={{ fontSize: 12.5, color: t.textMuted, margin: '0 0 18px' }}>
                    {mode === 'login' ? 'Sign in to access your workspace.' : mode === 'create' ? 'Set up a new RooCAD profile.' : 'Enter your account email and a new password.'}
                </p>
                <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ position: 'relative' }}>
                        <Mail size={16} style={{ position: 'absolute', left: 14, top: 14, color: t.textMuted }} />
                        <input type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ position: 'relative' }}>
                        <KeyRound size={16} style={{ position: 'absolute', left: 14, top: 14, color: t.textMuted }} />
                        <input type={show ? 'text' : 'password'} required placeholder={mode === 'reset' ? 'New password' : 'Password'} value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...inputStyle, paddingRight: 42 }} />
                        <button type="button" onClick={() => setShow(!show)} style={{ position: 'absolute', right: 10, top: 11, background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, display: 'flex' }}>{show ? <EyeOff size={17} /> : <Eye size={17} />}</button>
                    </div>
                    {mode !== 'login' && (
                        <div style={{ position: 'relative' }}>
                            <ShieldCheck size={16} style={{ position: 'absolute', left: 14, top: 14, color: t.textMuted }} />
                            <input type={show ? 'text' : 'password'} required placeholder="Confirm password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={inputStyle} />
                        </div>
                    )}
                    {error && <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', color: '#f87171', fontSize: 12, padding: '9px 12px', borderRadius: 9 }}>{error}</div>}
                    {info && <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#4ade80', fontSize: 12, padding: '9px 12px', borderRadius: 9 }}>{info}</div>}
                    <button type="submit" style={{ marginTop: 4, padding: 13, borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, color: '#fff', background: 'linear-gradient(135deg, #2563eb, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        {mode === 'login' ? <><LogIn size={16} /> Sign In</> : mode === 'create' ? <><UserPlus size={16} /> Create Account</> : <><KeyRound size={16} /> Reset Password</>}
                    </button>
                </form>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
                    {mode !== 'create'
                        ? <button type="button" onClick={() => { setMode('create'); setError(''); setInfo(''); }} style={link}>Create account</button>
                        : <button type="button" onClick={() => { setMode('login'); setError(''); setInfo(''); }} style={link}>Back to login</button>}
                    {mode !== 'reset'
                        ? <button type="button" onClick={() => { setMode('reset'); setError(''); setInfo(''); }} style={link}>Forgot password?</button>
                        : <button type="button" onClick={() => { setMode('login'); setError(''); setInfo(''); }} style={link}>Back to login</button>}
                </div>
            </div>
        </div>
    );
}
const link = { background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 };

